<template>
  <div class="power-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$y = x^α$</div>
    </div>

    <div class="split-layout">
      <!-- 曲线渲染 -->
      <div class="simulation-stage dark-stage flex-1">
        <svg width="100%" height="340" viewBox="-50 -50 300 300" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="pow-glow-p" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
           </defs>

           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 11" :key="'v'+i" :x1="-50 + (i-1)*30" y1="-50" :x2="-50 + (i-1)*30" y2="250" />
              <line v-for="i in 11" :key="'h'+i" x1="-50" :y1="-50 + (i-1)*30" x2="250" :y2="-50 + (i-1)*30" />
           </g>
           
           <line x1="-50" y1="200" x2="250" y2="200" stroke="#475569" stroke-width="2.5" />
           <line x1="0" y1="-50" x2="0" y2="250" stroke="#475569" stroke-width="2.5" />
           <text x="230" y="220" fill="#94a3b8" font-size="14" font-weight="900">X轴</text>
           <text x="10" y="-30" fill="#94a3b8" font-size="14" font-weight="900">Y轴</text>

           <path :d="curvePath" fill="none" stroke="#f43f5e" stroke-width="4" filter="url(#pow-glow-p)" stroke-linecap="round" />
           <circle cx="100" cy="100" r="5" fill="#fbbf24" stroke="#fff" stroke-width="1.5"/>
           <text x="110" y="115" fill="#fbbf24" font-size="13" font-weight="900">(1,1)</text>

           <g :transform="`translate(${currentX * 100}, ${200 - currentY * 100})`">
              <circle r="6" fill="#f43f5e" stroke="#fff" stroke-width="2" />
              <line x1="0" y1="0" :x2="0" :y2="currentY * 100" stroke="#94a3b8" stroke-dasharray="4,4" opacity="0.6" />
              <line x1="0" y1="0" :x2="-currentX * 100" y2="0" stroke="#94a3b8" stroke-dasharray="4,4" opacity="0.6" />
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item red">
              <label>因变量 y</label>
              <span class="val">{{ currentY.toFixed(2) }}</span>
           </div>
        </div>
      </div>

      <!-- 几何物理映射渲染 -->
      <div class="simulation-stage logic-panel">
        <div class="logic-header">维度演化模拟 - 比例缩放关系</div>
        <div class="logic-body">
           <svg width="100%" height="300" viewBox="0 0 300 300">
              <!-- 根据Alpha的不同，展示不同的形状 -->
              <!-- alpha = 1: 一维直线 -->
              <g v-if="alpha === 1" transform="translate(50, 150)">
                 <line x1="0" y1="0" :x2="currentY * 80" y2="0" stroke="#2dd4bf" stroke-width="12" stroke-linecap="round" />
                 <text :x="currentY * 40" y="-15" fill="#5eead4" text-anchor="middle" font-weight="bold">长度 (1D)</text>
              </g>

              <!-- alpha = 2: 二维正方形 -->
              <g v-else-if="alpha === 2" transform="translate(50, 150)">
                 <rect x="0" y="0" :width="currentX * 80" :height="currentX * 80" fill="rgba(244, 63, 94, 0.4)" stroke="#f43f5e" stroke-width="3" />
                 <text :x="currentX * 40" y="-10" fill="#fda4af" text-anchor="middle" font-weight="bold">面积 (2D)</text>
              </g>

              <!-- alpha = 3: 三维立方体模拟 (等距视角) -->
              <g v-else-if="alpha === 3" transform="translate(100, 150)">
                 <path :d="drawCube(currentX * 50)" fill="rgba(168, 85, 247, 0.4)" stroke="#d946ef" stroke-width="2" />
                 <text x="0" y="-10" fill="#e879f9" text-anchor="middle" font-weight="bold">体积 (3D) Y=X³</text>
              </g>

              <g v-else transform="translate(150, 150)">
                 <circle :r="Math.max(1, currentY * 20)" fill="rgba(251, 191, 36, 0.4)" stroke="#fbbf24" stroke-width="2" />
                 <text x="0" y="-30" fill="#fde68a" text-anchor="middle" font-weight="bold">泛化引力场域尺度</text>
              </g>
           </svg>
        </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
        <div class="control-grid-mini">
           <div class="s-item">
              <label>维度/指数 α (展示值为整数)</label>
              <el-slider v-model="alpha" :min="-2" :max="3" :step="1" size="small"></el-slider>
           </div>
           <div class="s-item">
              <label>自变量 (输入维度) x: {{ currentX.toFixed(1) }}</label>
              <el-slider v-model="currentX" :min="0.1" :max="2.5" :step="0.1" size="small"></el-slider>
           </div>
        </div>
      </div>

      <div class="discovery-card-compact" v-latex>
         <i class="el-icon-picture-outline" style="color:#f43f5e;"></i>
         <div class="discovery-content">
            <p><strong>几何与幂：</strong>幂函数揭示了尺度如何随维度扩展。α=1 代表线性关系，α=2 代表面积与边长的平方关系，α=3 象征三维空间的体积爆发现象！底数大于 0 时，图象恒过第一象限。万有引力、静电力则是 α=-2 的经典应用。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PowerFunctionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      alpha: 2,
      currentX: 1.5
    }
  },
  computed: {
    currentY() {
      return Math.pow(this.currentX, this.alpha);
    },
    curvePath() {
      let pathArr = [];
      for (let x = 0.05; x <= 2.8; x += 0.05) {
        let y = Math.pow(x, this.alpha);
        if (y > 3) continue;
        pathArr.push(`${(x * 100).toFixed(1)},${(200 - y * 100).toFixed(1)}`);
      }
      return `M ${pathArr.join(' L ')}`;
    }
  },
  methods: {
    drawCube(size) {
      // 简单等距投影立方体路径
      const s = size;
      const dx = s * 0.866;
      const dy = s * 0.5;
      
      // M x y L... Z
      const front = `M 0 0 L 0 ${s} L ${dx} ${s-dy} L ${dx} ${-dy} Z`;
      const top = `M 0 0 L ${dx} ${-dy} L 0 ${-dy*2} L ${-dx} ${-dy} Z`;
      const side = `M 0 0 L ${-dx} ${-dy} L ${-dx} ${s-dy} L 0 ${s} Z`;
      
      return `${front} ${top} ${side}`;
    }
  }
}
</script>

<style scoped lang="scss">
.power-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #fff1f2; color: #be123c; border: 1px solid #fecdd3; padding: 6px 16px; border-radius: 20px; font-weight: 900; font-family: monospace; font-size: 15px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 340px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; }
.logic-header { background: #1e293b; padding: 10px; color: #fbbf24; font-size: 13px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; }
.logic-body { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; } .val { font-size: 15px; color: #f8fafc; font-weight: 900; font-family: monospace; } &.red { border-left: 4px solid #f43f5e; } } } }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #fff1f2; padding: 14px 18px; border-radius: 12px; border: 1px solid #ffe4e6; display: flex; align-items: flex-start; gap: 12px; i { font-size: 1.2rem; margin-top: 2px; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #9f1239; line-height: 1.6; strong { color: #881337; font-weight: 900; } } } }
</style>
